import { z } from 'zod';
import { IdSchema, InstantSchema } from '../common/index.js';
import { CODES_ROLES } from '../permissions/index.js';

/** Identité, sessions, impersonation, études, système (architecture §2.1, §2.2, §3.3 auth/impersonation/admin/system). */

export const RoleSchema = z.enum(CODES_ROLES).meta({ id: 'Role', description: 'Rôle Lakou.' });

export const RoleAssignmentSchema = z
  .object({
    role: RoleSchema,
    scopeType: z.enum(['global', 'dossier', 'etude', 'mission']),
    scopeId: IdSchema.nullable(),
    validUntil: InstantSchema.nullable(),
  })
  .meta({ id: 'RoleAssignment' });

export const UserSchema = z
  .object({
    id: IdSchema,
    email: z.email(),
    displayName: z.string().min(1),
    kind: z.enum(['staff', 'external', 'portal']),
    status: z.enum(['actif', 'suspendu', 'invite']),
    roles: z.array(RoleAssignmentSchema),
  })
  .meta({ id: 'User', description: 'Utilisateur (équipe, externe ou portail).' });

export const UserCreateSchema = z
  .object({
    email: z.email(),
    displayName: z.string().min(1),
    kind: z.enum(['staff', 'external', 'portal']),
    roles: z.array(RoleAssignmentSchema.omit({ validUntil: true })).min(1),
  })
  .meta({ id: 'UserCreate' });

export const ImpersonationSummarySchema = z
  .object({
    id: IdSchema,
    mode: z.enum(['read', 'act']),
    targetDisplayName: z.string(),
    startedAt: InstantSchema,
  })
  .meta({ id: 'ImpersonationSummary', description: 'Bandeau « Vous voyez Lakou en tant que … ».' });

export const SessionSchema = z
  .object({
    user: UserSchema,
    effectiveUser: UserSchema.nullable(),
    impersonation: ImpersonationSummarySchema.nullable(),
    permissions: z.array(z.string()),
    expiresAt: InstantSchema,
  })
  .meta({ id: 'Session', description: 'Session courante ; effectiveUser est la personne incarnée.' });

export const LoginSchema = z
  .object({
    email: z.email(),
    password: z.string().min(8),
    totp: z
      .string()
      .regex(/^\d{6}$/)
      .optional(),
  })
  .meta({ id: 'Login' });

export const MagicLinkSchema = z
  .object({ token: z.string().min(4) })
  .meta({ id: 'MagicLink', description: 'Jeton ou code court du lien.' });

export const OtpRequestSchema = z.object({ channel: z.enum(['sms', 'whatsapp', 'email']) }).meta({ id: 'OtpRequest' });
export const OtpVerifySchema = z.object({ code: z.string().regex(/^\d{6}$/) }).meta({ id: 'OtpVerify' });
export const OtpChallengeSchema = z.object({ challengeId: IdSchema, expiresAt: InstantSchema }).meta({ id: 'OtpChallenge' });

export const ImpersonationStartSchema = z
  .object({ targetUserId: IdSchema, reason: z.string().min(3), mode: z.enum(['read', 'act']).default('read') })
  .meta({ id: 'ImpersonationStart' });

export const ImpersonationSessionSchema = z
  .object({
    id: IdSchema,
    actorUserId: IdSchema,
    targetUserId: IdSchema,
    reason: z.string(),
    mode: z.enum(['read', 'act']),
    startedAt: InstantSchema,
    endedAt: InstantSchema.nullable(),
    endedReason: z.string().nullable(),
  })
  .meta({ id: 'ImpersonationSession' });

export const ImpersonationConfirmSchema = z
  .object({ action: z.string().min(3), payloadHash: z.string().regex(/^[a-f0-9]{64}$/) })
  .meta({ id: 'ImpersonationConfirm', description: 'Confirmation explicite d’une action en tant que.' });

export const ImpersonationConfirmationSchema = z
  .object({ confirmationId: IdSchema, validUntil: InstantSchema })
  .meta({ id: 'ImpersonationConfirmation' });

export const EtudeMemberSchema = z
  .object({ userId: IdSchema, displayName: z.string(), function: z.enum(['clerc', 'notaire', 'gestion']) })
  .meta({ id: 'EtudeMember' });

export const EtudeSchema = z
  .object({
    id: IdSchema,
    name: z.string().min(1),
    address: z.string().nullable(),
    notaireTitulaire: z.string().nullable(),
    status: z.enum(['active', 'inactive']),
    members: z.array(EtudeMemberSchema),
    rowVersion: z.number().int().min(0),
  })
  .meta({ id: 'Etude', description: 'Étude notariale (ajoutable, modifiable, supprimable).' });

export const EtudeWriteSchema = z
  .object({ name: z.string().min(1), address: z.string().nullable(), notaireTitulaire: z.string().nullable() })
  .meta({ id: 'EtudeWrite' });

export const HealthSchema = z.object({ status: z.literal('ok'), service: z.string() }).meta({ id: 'Health' });

export const VersionSchema = z
  .object({
    service: z.string(),
    commit: z.string(),
    commitCourt: z.string(),
    branch: z.string(),
    buildDate: z.string(),
    environment: z.string(),
    container: z.string(),
  })
  .meta({ id: 'Version', description: 'Identité du build (commit, branche, date, environnement, conteneur).' });

export const AcceptedSchema = z.object({ received: z.literal(true) }).meta({ id: 'Accepted' });
